import cx from 'classnames';
import FocusTrap from 'focus-trap-react';
import type { MouseEvent, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from './useEscapeKey.js';
import { usePortal } from './usePortal.js';
import { useScrollLock } from './useScrollLock.js';

export interface SharedProps {
  onCloseHandler?: (e?: MouseEvent) => void;
  isOpen: boolean;
}

export interface ModalComponentProps
  extends
    SharedProps,
    Pick<
      FocusTrap.Props,
      'active' | 'paused' | 'focusTrapOptions' | 'containerElements'
    > {
  className?: string;
  children: ReactNode;
  pauseFocus?: boolean;
  canCloseFromOutside?: boolean;
  type?: 'default' | 'image';
  /**
   * Called whenever a close attempt is made (ESC key, outside click, close
   * button), regardless of `canCloseFromOutside`. Useful for tracking user
   * intent to close without interfering with the controlled `isOpen` state.
   */
  onCloseAttempt?: () => void;
}

export default function ModalComponent({
  className,
  children = null,
  isOpen = false,
  onCloseHandler = () => {},
  pauseFocus: _pauseFocus,
  canCloseFromOutside = true,
  type = 'default',
  onCloseAttempt,
  ...focusTrapProps
}: ModalComponentProps) {
  const modalId = `__modal__${useId()}`;
  const wrapRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  // Keep latest prop values in refs so handleCloseAttempt can have a stable
  // identity (useCallback with []) while always calling the current functions.
  // Refs are synced in effects to avoid writing during the render phase.
  const onCloseHandlerRef = useRef(onCloseHandler);
  useEffect(() => {
    onCloseHandlerRef.current = onCloseHandler;
  }, [onCloseHandler]);

  const onCloseAttemptRef = useRef(onCloseAttempt);
  useEffect(() => {
    onCloseAttemptRef.current = onCloseAttempt;
  }, [onCloseAttempt]);

  const { portalEl, mounted } = usePortal(modalId);

  // Stable identity: created once, reads latest handler values via refs.
  const handleCloseAttempt = useCallback((e?: MouseEvent) => {
    onCloseAttemptRef.current?.();
    onCloseHandlerRef.current?.(e);
  }, []);

  useEscapeKey(isOpen, handleCloseAttempt);
  useScrollLock(isOpen);

  function onExternalCloseHandler(e: MouseEvent) {
    const { target } = e;
    const isOutsideTarget =
      target === wrapRef.current ||
      target === containerRef.current ||
      target === contentRef.current ||
      target === bgRef.current;

    if (!isOutsideTarget) {
      return;
    }

    // Always notify on outside-click attempt, regardless of canCloseFromOutside.
    onCloseAttemptRef.current?.();

    if (canCloseFromOutside) {
      onCloseHandlerRef.current?.(e);
    }
  }

  // Stable context value: only changes when isOpen changes, since
  // handleCloseAttempt has a stable identity.
  const contextValue = useMemo(
    () => ({ isOpen, onCloseHandler: handleCloseAttempt }),
    [isOpen, handleCloseAttempt],
  );

  // portalEl is null on the server (document unavailable). FocusTrap is only
  // activated once `mounted` is true, meaning the portal has been appended to
  // the document. This ensures that DOM queries used by focus-trap options
  // (e.g. initialFocus/fallbackFocus CSS selectors) resolve correctly because
  // the portal subtree is attached before the trap activates.
  return isOpen && portalEl
    ? createPortal(
        <ModalContext.Provider value={contextValue}>
          <FocusTrap
            {...focusTrapProps}
            active={mounted && (focusTrapProps.active ?? true)}
            focusTrapOptions={{
              ...focusTrapProps.focusTrapOptions,
              // if children contain no interactive elements the
              // trap falls back to this element instead of throwing.
              // tabIndex=-1 makes it focusable by script but keeps it out of
              // the normal tab order.
              fallbackFocus:
                focusTrapProps.focusTrapOptions?.fallbackFocus ??
                '.mftfallback',
            }}
          >
            <div
              className={cx('modal__outer', className, {
                'modal--image': type === 'image',
              })}
              onClick={onExternalCloseHandler}
            >
              <div
                ref={bgRef}
                className={cx('modal__bg', {
                  'modal--image__bg': type === 'image',
                })}
              />
              <div
                ref={wrapRef}
                className={cx('modal__wrap', {
                  'modal--image__wrap': type === 'image',
                })}
              >
                <div
                  ref={containerRef}
                  className={cx('modal__container', {
                    'modal--image__container': type === 'image',
                  })}
                >
                  <div
                    ref={contentRef}
                    className={cx('modal__content', {
                      'modal--image__content': type === 'image',
                    })}
                  >
                    <div tabIndex={-1} className={cx('mftfallback')} />
                    {children}
                  </div>
                </div>
              </div>
            </div>
          </FocusTrap>
        </ModalContext.Provider>,
        portalEl,
      )
    : null;
}

const ModalContext = createContext<SharedProps | undefined>(undefined);

export function useModalContext() {
  const context = useContext(ModalContext);

  if (context === undefined) {
    throw new Error(
      'useModalContext must be used within a ModalContextProvider',
    );
  }

  return context;
}
